// @flow
import * as Immutable from 'immutable'; // This is required
import ImmutableModel from './ImmutableModel';

import { SkillId } from './SkillId';
import { TransporterId } from './TransporterId';
import { MasterTransporter } from './MasterTransporter';
import { RiderPersonaLimits } from './RiderPersonaLimits';
import { VehicleLoadingParams } from './VehicleLoadingParams';
import { Volume } from './Volume';
import { Dimensions } from './Dimensions';
import { AuditMetadata } from './AuditMetadata';
import { VehicleCompartment } from './VehicleCompartment';

import type { SkillIdModelType } from './SkillId';
import type { VolumeModelType } from './Volume';
import type { DimensionsModelType } from './Dimensions';
import type { RiderPersonaLimitsModelType } from './RiderPersonaLimits';
import type { TransporterIdModelType } from './TransporterId';
import type { MasterTransporterModelType } from './MasterTransporter';
import type { VehicleLoadingParamsModelType } from './VehicleLoadingParams';
import type { AuditMetadataModelType } from './AuditMetadata';
import type { VehicleTravelParamsModelType } from './VehicleTravelParams';
import type { VehicleCompartmentModelType } from './VehicleCompartment';

export type StatusEnum = 'ACTIVE' | 'INACTIVE';
export type FuelTypeEnum = 'PETROL' | 'DIESEL';
export type FleetTypeEnum = 'CAPTIVE' | 'THIRD_PARTY' | 'ADHOC';
export type LoadingUnloadingSideEnum = 'REAR' | 'RIGHT' | 'LEFT' | 'TOP';
export type VehicleTypeEnum =
    | 'NONE'
    | 'TRUCK'
    | 'BUS'
    | 'BIKE'
    | 'CAR'
    | 'PERSON'
    | 'TRICYCLE'
    | 'BICYCLE';

export type MasterVehicleModelModelType = {
    clientId: string,
    id: string,
    code: string,
    name: string,
    status: StatusEnum,
    description: string,
    transporterId: TransporterIdModelType,
    transporter: MasterTransporterModelType,
    skills: ?Array<SkillIdModelType>,
    fleetType: FleetTypeEnum,
    isDeepFreeze: boolean,
    doorWidth: number,
    doorHeight: number,
    unloadingSide: LoadingUnloadingSideEnum,
    limits: ?RiderPersonaLimitsModelType,
    loadingParams: ?VehicleLoadingParamsModelType,
    vehicleType?: ?VehicleTypeEnum,
    volume: VolumeModelType,
    vehicleVolume: ?VolumeModelType,
    maxPayloadWeight: number,
    dimensions: ?DimensionsModelType,
    containerDimensions: ?DimensionsModelType,
    userCount: number,
    allowMultiTrip: boolean,
    limitMultiTrip: boolean,
    maxAdditionalTrips: number,
    isColdChain: boolean,
    unladenWeight: number,
    fuelType: FuelTypeEnum,
    travelParams: ?VehicleTravelParamsModelType,
    auditMetadata: AuditMetadataModelType,
    version: number,
    customProperties: { [key: string]: string },
    compartments: Array<VehicleCompartmentModelType>
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class MasterVehicleModel extends ImmutableModel {
    static fromJS(json: MasterVehicleModelModelType): MasterVehicleModel {
        const state: Object = Object.assign({}, json);
        state.transporterId = TransporterId.fromJS(state.transporterId);
        state.transporter = MasterTransporter.fromJS(state.transporter);
        state.skills = state.skills
            ? Immutable.List(state.skills).map(item => SkillId.fromJS(item))
            : state.skills;
        state.limits = state.limits
            ? RiderPersonaLimits.fromJS(state.limits)
            : state.limits;
        state.loadingParams = state.loadingParams
            ? VehicleLoadingParams.fromJS(state.loadingParams)
            : state.loadingParams;
        state.volume = Volume.fromJS(state.volume);
        state.vehicleVolume = state.vehicleVolume
            ? Volume.fromJS(state.vehicleVolume)
            : state.vehicleVolume;
        state.dimensions = state.dimensions
            ? Dimensions.fromJS(state.dimensions)
            : state.dimensions;
        state.containerDimensions = state.containerDimensions
            ? Dimensions.fromJS(state.containerDimensions)
            : state.containerDimensions;
        state.travelParams = state.travelParams
            ? VehicleTravelParams.fromJS(state.travelParams)
            : state.travelParams;
        state.auditMetadata = AuditMetadata.fromJS(state.auditMetadata);
        state.customProperties = Immutable.Map(state.customProperties);
        state.compartments = Immutable.List(state.compartments).map(item =>
            VehicleCompartment.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    toJS(): MasterVehicleModelModelType {
        const js: MasterVehicleModelModelType = {
            clientId: this.clientId,
            id: this.id,
            code: this.code,
            name: this.name,
            status: this.status,
            description: this.description,
            transporterId: this.transporterId.toJS(),
            transporter: this.transporter.toJS(),
            skills: this.skills
                ? this.skills.toArray().map(item => item.toJS())
                : this.skills,
            fleetType: this.fleetType,
            isDeepFreeze: this.isDeepFreeze,
            doorWidth: this.doorWidth,
            doorHeight: this.doorHeight,
            unloadingSide: this.unloadingSide,
            limits: this.limits ? this.limits.toJS() : this.limits,
            loadingParams: this.loadingParams
                ? this.loadingParams.toJS()
                : this.loadingParams,
            volume: this.volume.toJS(),
            vehicleVolume: this.vehicleVolume
                ? this.vehicleVolume.toJS()
                : this.vehicleVolume,
            maxPayloadWeight: this.maxPayloadWeight,
            dimensions: this.dimensions
                ? this.dimensions.toJS()
                : this.dimensions,
            containerDimensions: this.containerDimensions
                ? this.containerDimensions.toJS()
                : this.containerDimensions,
            userCount: this.userCount,
            allowMultiTrip: this.allowMultiTrip,
            limitMultiTrip: this.limitMultiTrip,
            maxAdditionalTrips: this.maxAdditionalTrips,
            isColdChain: this.isColdChain,
            unladenWeight: this.unladenWeight,
            fuelType: this.fuelType,
            travelParams: this.travelParams
                ? this.travelParams.toJS()
                : this.travelParams,
            auditMetadata: this.auditMetadata.toJS(),
            version: this.version,
            customProperties: this.customProperties.toObject(),
            compartments: this.compartments.toArray().map(item => item.toJS())
        };

        if (this.vehicleType != null) {
            js.vehicleType = this.vehicleType;
        }

        return js;
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get code(): string {
        return this._state.get('code');
    }

    setCode(code: string): this {
        return this.clone(this._state.set('code', code));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get status(): StatusEnum {
        return this._state.get('status');
    }

    setStatus(status: StatusEnum): this {
        return this.clone(this._state.set('status', status));
    }

    get description(): string {
        return this._state.get('description');
    }

    setDescription(description: string): this {
        return this.clone(this._state.set('description', description));
    }

    get transporterId(): TransporterId {
        return this._state.get('transporterId');
    }

    setTransporterId(transporterId: TransporterId): this {
        return this.clone(this._state.set('transporterId', transporterId));
    }

    get transporter(): MasterTransporter {
        return this._state.get('transporter');
    }

    setTransporter(transporter: MasterTransporter): this {
        return this.clone(this._state.set('transporter', transporter));
    }

    get skills(): ?Immutable.List<SkillId> {
        return this._state.get('skills');
    }

    setSkills(skills: ?Immutable.List<SkillId>): this {
        return this.clone(this._state.set('skills', skills));
    }

    get fleetType(): FleetTypeEnum {
        return this._state.get('fleetType');
    }

    setFleetType(fleetType: FleetTypeEnum): this {
        return this.clone(this._state.set('fleetType', fleetType));
    }

    get isDeepFreeze(): boolean {
        return this._state.get('isDeepFreeze');
    }

    setIsDeepFreeze(isDeepFreeze: boolean): this {
        return this.clone(this._state.set('isDeepFreeze', isDeepFreeze));
    }

    get doorWidth(): number {
        return this._state.get('doorWidth');
    }

    setDoorWidth(doorWidth: number): this {
        return this.clone(this._state.set('doorWidth', doorWidth));
    }

    get doorHeight(): number {
        return this._state.get('doorHeight');
    }

    setDoorHeight(doorHeight: number): this {
        return this.clone(this._state.set('doorHeight', doorHeight));
    }

    get unloadingSide(): LoadingUnloadingSideEnum {
        return this._state.get('unloadingSide');
    }

    setUnloadingSide(unloadingSide: LoadingUnloadingSideEnum): this {
        return this.clone(this._state.set('unloadingSide', unloadingSide));
    }

    get limits(): ?RiderPersonaLimits {
        return this._state.get('limits');
    }

    setLimits(limits: ?RiderPersonaLimits): this {
        return this.clone(this._state.set('limits', limits));
    }

    get loadingParams(): ?VehicleLoadingParams {
        return this._state.get('loadingParams');
    }

    setLoadingParams(loadingParams: ?VehicleLoadingParams): this {
        return this.clone(this._state.set('loadingParams', loadingParams));
    }

    get vehicleType(): ?VehicleTypeEnum {
        return this._state.get('vehicleType');
    }

    setVehicleType(vehicleType: ?VehicleTypeEnum): this {
        return this.clone(this._state.set('vehicleType', vehicleType));
    }

    get volume(): Volume {
        return this._state.get('volume');
    }

    setVolume(volume: Volume): this {
        return this.clone(this._state.set('volume', volume));
    }

    get vehicleVolume(): ?Volume {
        return this._state.get('vehicleVolume');
    }

    setVehicleVolume(vehicleVolume: ?Volume): this {
        return this.clone(this._state.set('vehicleVolume', vehicleVolume));
    }

    get maxPayloadWeight(): number {
        return this._state.get('maxPayloadWeight');
    }

    setMaxPayloadWeight(maxPayloadWeight: number): this {
        return this.clone(
            this._state.set('maxPayloadWeight', maxPayloadWeight)
        );
    }

    get dimensions(): ?Dimensions {
        return this._state.get('dimensions');
    }

    setDimensions(dimensions: ?Dimensions): this {
        return this.clone(this._state.set('dimensions', dimensions));
    }

    get containerDimensions(): ?Dimensions {
        return this._state.get('containerDimensions');
    }

    setContainerDimensions(containerDimensions: ?Dimensions): this {
        return this.clone(
            this._state.set('containerDimensions', containerDimensions)
        );
    }

    get userCount(): number {
        return this._state.get('userCount');
    }

    setUserCount(userCount: number): this {
        return this.clone(this._state.set('userCount', userCount));
    }

    get allowMultiTrip(): boolean {
        return this._state.get('allowMultiTrip');
    }

    setAllowMultiTrip(allowMultiTrip: boolean): this {
        return this.clone(this._state.set('allowMultiTrip', allowMultiTrip));
    }

    get limitMultiTrip(): boolean {
        return this._state.get('limitMultiTrip');
    }

    setLimitMultiTrip(limitMultiTrip: boolean): this {
        return this.clone(this._state.set('limitMultiTrip', limitMultiTrip));
    }

    get maxAdditionalTrips(): number {
        return this._state.get('maxAdditionalTrips');
    }

    setMaxAdditionalTrips(maxAdditionalTrips: number): this {
        return this.clone(
            this._state.set('maxAdditionalTrips', maxAdditionalTrips)
        );
    }

    get isColdChain(): boolean {
        return this._state.get('isColdChain');
    }

    setIsColdChain(isColdChain: boolean): this {
        return this.clone(this._state.set('isColdChain', isColdChain));
    }

    get unladenWeight(): number {
        return this._state.get('unladenWeight');
    }

    setUnladenWeight(unladenWeight: number): this {
        return this.clone(this._state.set('unladenWeight', unladenWeight));
    }

    get fuelType(): FuelTypeEnum {
        return this._state.get('fuelType');
    }

    setFuelType(fuelType: FuelTypeEnum): this {
        return this.clone(this._state.set('fuelType', fuelType));
    }

    get travelParams(): ?VehicleTravelParams {
        return this._state.get('travelParams');
    }

    setTravelParams(travelParams: ?VehicleTravelParams): this {
        return this.clone(this._state.set('travelParams', travelParams));
    }

    get auditMetadata(): AuditMetadata {
        return this._state.get('auditMetadata');
    }

    setAuditMetadata(auditMetadata: AuditMetadata): this {
        return this.clone(this._state.set('auditMetadata', auditMetadata));
    }

    get version(): number {
        return this._state.get('version');
    }

    setVersion(version: number): this {
        return this.clone(this._state.set('version', version));
    }

    get customProperties(): Immutable.Map<string, string> {
        return this._state.get('customProperties');
    }

    setCustomProperties(customProperties: Immutable.Map<string, string>): this {
        return this.clone(
            this._state.set('customProperties', customProperties)
        );
    }

    get compartments(): Immutable.List<VehicleCompartment> {
        return this._state.get('compartments');
    }

    setCompartments(compartments: Immutable.List<VehicleCompartment>): this {
        return this.clone(this._state.set('compartments', compartments));
    }
}
